import React from 'react'
import QuestionCard from './QuestionCard'
import RadioOption from './RadioOption'
import CheckboxOption from './CheckboxOption'

interface SectionProps {
  formData: Record<string, any>
  updateField: (field: string, value: any) => void
}

/**
 * Section B — AI Usage (Q5-Q8).
 * Q5 is always shown. Q6-Q8 are conditionally shown only when the
 * respondent uses AI (q5 !== 'nao_usamos').
 */
const SectionB: React.FC<SectionProps> = ({ formData, updateField }) => {
  const usesAI = formData.q5 && formData.q5 !== 'nao_usamos'

  const handleCheckboxChange = (field: string, value: string, checked: boolean) => {
    const current: string[] = formData[field] || []
    const updated = checked
      ? [...current, value]
      : current.filter((v: string) => v !== value)
    updateField(field, updated)
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Q5 — Usa IA? */}
      <QuestionCard
        title="Sua empresa usa alguma ferramenta de IA na operação de e-commerce?"
        required
      >
        <RadioOption
          name="q5"
          value="usamos_ativamente"
          label="Sim, usamos ativamente"
          selected={formData.q5 === 'usamos_ativamente'}
          onChange={(v) => updateField('q5', v)}
        />
        <RadioOption
          name="q5"
          value="experimentando"
          label="Estamos experimentando"
          selected={formData.q5 === 'experimentando'}
          onChange={(v) => updateField('q5', v)}
        />
        <RadioOption
          name="q5"
          value="tentamos_paramos"
          label="Já tentamos mas paramos"
          selected={formData.q5 === 'tentamos_paramos'}
          onChange={(v) => updateField('q5', v)}
        />
        <RadioOption
          name="q5"
          value="nao_usamos"
          label="Não usamos"
          selected={formData.q5 === 'nao_usamos'}
          onChange={(v) => updateField('q5', v)}
        />
      </QuestionCard>

      {/* Q6 — Áreas de uso de IA (conditional) */}
      {usesAI && (
        <QuestionCard title="Em quais áreas você usa IA hoje?">
          <CheckboxOption
            name="q6"
            value="descricoes_textos"
            label="Descrições e textos de produtos"
            checked={(formData.q6 || []).includes('descricoes_textos')}
            onChange={(v, c) => handleCheckboxChange('q6', v, c)}
          />
          <CheckboxOption
            name="q6"
            value="imagens_produtos"
            label="Imagens de produtos (geração ou edição)"
            checked={(formData.q6 || []).includes('imagens_produtos')}
            onChange={(v, c) => handleCheckboxChange('q6', v, c)}
          />
          <CheckboxOption
            name="q6"
            value="precificacao"
            label="Precificação e monitoramento de preços"
            checked={(formData.q6 || []).includes('precificacao')}
            onChange={(v, c) => handleCheckboxChange('q6', v, c)}
          />
          <CheckboxOption
            name="q6"
            value="anuncios_trafego"
            label="Anúncios e tráfego pago (Meta, Google, etc.)"
            checked={(formData.q6 || []).includes('anuncios_trafego')}
            onChange={(v, c) => handleCheckboxChange('q6', v, c)}
          />
          <CheckboxOption
            name="q6"
            value="atendimento"
            label="Atendimento ao cliente (chatbots, respostas)"
            checked={(formData.q6 || []).includes('atendimento')}
            onChange={(v, c) => handleCheckboxChange('q6', v, c)}
          />
          <CheckboxOption
            name="q6"
            value="emails_crm"
            label="Emails e CRM"
            checked={(formData.q6 || []).includes('emails_crm')}
            onChange={(v, c) => handleCheckboxChange('q6', v, c)}
          />
          <CheckboxOption
            name="q6"
            value="analise_dados"
            label="Análise de dados e relatórios"
            checked={(formData.q6 || []).includes('analise_dados')}
            onChange={(v, c) => handleCheckboxChange('q6', v, c)}
          />
          <CheckboxOption
            name="q6"
            value="gestao_estoque"
            label="Gestão de estoque e previsão de demanda"
            checked={(formData.q6 || []).includes('gestao_estoque')}
            onChange={(v, c) => handleCheckboxChange('q6', v, c)}
          />
          <CheckboxOption
            name="q6"
            value="seo"
            label="SEO"
            checked={(formData.q6 || []).includes('seo')}
            onChange={(v, c) => handleCheckboxChange('q6', v, c)}
          />
          <CheckboxOption
            name="q6"
            value="gestao_marketplaces"
            label="Gestão de marketplaces"
            checked={(formData.q6 || []).includes('gestao_marketplaces')}
            onChange={(v, c) => handleCheckboxChange('q6', v, c)}
          />
          <CheckboxOption
            name="q6"
            value="reclame_aqui"
            label="Reclame Aqui / reputação"
            checked={(formData.q6 || []).includes('reclame_aqui')}
            onChange={(v, c) => handleCheckboxChange('q6', v, c)}
          />
          <CheckboxOption
            name="q6"
            value="outra"
            label="Outra"
            checked={(formData.q6 || []).includes('outra')}
            onChange={(v, c) => handleCheckboxChange('q6', v, c)}
            hasOtherField
            otherValue={formData.q6_other || ''}
            onOtherChange={(v) => updateField('q6_other', v)}
          />
        </QuestionCard>
      )}

      {/* Q7 — Ferramentas de IA (conditional) */}
      {usesAI && (
        <QuestionCard title="Quais ferramentas de IA sua equipe usa?">
          <CheckboxOption
            name="q7"
            value="chatgpt_llm_geral"
            label="ChatGPT / GPT (uso geral)"
            checked={(formData.q7 || []).includes('chatgpt_llm_geral')}
            onChange={(v, c) => handleCheckboxChange('q7', v, c)}
          />
          <CheckboxOption
            name="q7"
            value="claude_gemini_llm"
            label="Claude / Gemini / outro LLM geral"
            checked={(formData.q7 || []).includes('claude_gemini_llm')}
            onChange={(v, c) => handleCheckboxChange('q7', v, c)}
          />
          <CheckboxOption
            name="q7"
            value="ferramentas_nativas_plataforma"
            label="Ferramentas nativas da plataforma (Nuvemshop AI, VTEX AI, etc.)"
            checked={(formData.q7 || []).includes('ferramentas_nativas_plataforma')}
            onChange={(v, c) => handleCheckboxChange('q7', v, c)}
          />
          <CheckboxOption
            name="q7"
            value="ferramentas_especializadas"
            label="Ferramentas especializadas de e-commerce (precificação, descrição, ads)"
            checked={(formData.q7 || []).includes('ferramentas_especializadas')}
            onChange={(v, c) => handleCheckboxChange('q7', v, c)}
          />
          <CheckboxOption
            name="q7"
            value="ferramentas_proprias"
            label="Ferramentas próprias / desenvolvidas internamente"
            checked={(formData.q7 || []).includes('ferramentas_proprias')}
            onChange={(v, c) => handleCheckboxChange('q7', v, c)}
          />
          <CheckboxOption
            name="q7"
            value="copilot_assistentes_codigo"
            label="Copilot / assistentes de código"
            checked={(formData.q7 || []).includes('copilot_assistentes_codigo')}
            onChange={(v, c) => handleCheckboxChange('q7', v, c)}
          />
          <CheckboxOption
            name="q7"
            value="outro"
            label="Outro"
            checked={(formData.q7 || []).includes('outro')}
            onChange={(v, c) => handleCheckboxChange('q7', v, c)}
            hasOtherField
            otherValue={formData.q7_other || ''}
            onOtherChange={(v) => updateField('q7_other', v)}
          />
        </QuestionCard>
      )}

      {/* Q8 — Nível de autonomia da IA (conditional) */}
      {usesAI && (
        <QuestionCard
          title="Qual o nível de autonomia da IA nas suas operações?"
        >
          <RadioOption
            name="q8"
            value="manual"
            label="Manual: a equipe usa IA como ferramenta de consulta (copia-cola do ChatGPT)"
            selected={formData.q8 === 'manual'}
            onChange={(v) => updateField('q8', v)}
          />
          <RadioOption
            name="q8"
            value="assistido"
            label="Assistido: IA gera sugestões que a equipe revisa e aplica"
            selected={formData.q8 === 'assistido'}
            onChange={(v) => updateField('q8', v)}
          />
          <RadioOption
            name="q8"
            value="semi_automatico"
            label="Semi-automático: IA executa tarefas com supervisão humana"
            selected={formData.q8 === 'semi_automatico'}
            onChange={(v) => updateField('q8', v)}
          />
          <RadioOption
            name="q8"
            value="automatico"
            label="Automático: IA executa tarefas de forma autônoma em algumas áreas"
            selected={formData.q8 === 'automatico'}
            onChange={(v) => updateField('q8', v)}
          />
        </QuestionCard>
      )}
    </div>
  )
}

export default SectionB
